import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, BarChart3, Trash2, ExternalLink, Rocket } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { assertPleskProvisionSucceeded } from "@/lib/pleskProvision";
import { buildDeployPayload } from "@/lib/deployHelper";

type Props = {
  siteId: string;
  domain: string | null;
  onDeployStarted: () => void;
  onDeleted: () => void;
};

export default function SiteActionCenter({ siteId, domain, onDeployStarted, onDeleted }: Props) {
  const navigate = useNavigate();

  const handleDeploy = async () => {
    if (!domain) {
      toast.error("No domain configured for this site");
      return;
    }
    try {
      const payload = await buildDeployPayload(siteId, domain);
      const { data, error } = await supabase.functions.invoke("plesk-provision", {
        body: payload,
      });
      if (error) throw error;
      assertPleskProvisionSucceeded(data);
      toast.success("Deployment completed");
      onDeployStarted();
    } catch (err: any) {
      toast.error(err.message || "Failed to start deployment");
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this site? This action cannot be undone.")) return;
    const { error } = await supabase.from("websites").delete().eq("id", siteId);
    if (error) {
      toast.error("Failed to delete site");
    } else {
      toast.success("Site deleted");
      onDeleted();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-md p-1 hover:bg-accent transition-colors" onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={handleDeploy}>
          <Rocket className="mr-2 h-4 w-4" /> Deploy
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/analytics?website=${siteId}`)}>
          <BarChart3 className="mr-2 h-4 w-4" /> Analytics
        </DropdownMenuItem>
        {domain && (
          <DropdownMenuItem onClick={() => window.open(`https://${domain}`, "_blank")}>
            <ExternalLink className="mr-2 h-4 w-4" /> Visit Site
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
          <Trash2 className="mr-2 h-4 w-4" /> Delete Site
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
